import { useState, useEffect } from 'react';
import { UserIcon } from 'lucide-react';
import { getFollowers } from '@/lib/api';
import type { Profile } from '@/types/database.types';
import Modal from '@/components/Modal';
import FollowButton from './FollowButton';
import Pagination from '@/components/Pagination';

interface FollowersModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  onCountChange?: (count: number) => void;
}

export default function FollowersModal({ isOpen, onClose, userId, onCountChange }: FollowersModalProps) {
  const [followers, setFollowers] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const fetchFollowers = async () => {
    if (!isOpen || !userId) return;
    
    try {
      setLoading(true);
      const response = await getFollowers(userId, currentPage);
      setFollowers(response.data);
      setTotalPages(Math.ceil(response.total / response.limit));
      onCountChange?.(response.total);
    } catch (error) {
      console.error('Error fetching followers:', error);
    } finally {
      setLoading(false);
    }
  };

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setCurrentPage(1);
      setFollowers([]);
      fetchFollowers();
    }
  }, [isOpen, userId]);

  // Fetch data when page changes
  useEffect(() => {
    if (isOpen) {
      fetchFollowers();
    }
  }, [currentPage]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Followers"
    >
      <div className="space-y-4">
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : followers.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No followers yet
          </div>
        ) : (
          <div className="space-y-4">
            {followers.map((follower) => (
              <div key={follower.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  {follower.avatar_url ? (
                    <img
                      src={follower.avatar_url}
                      alt={follower.username}
                      className="h-10 w-10 rounded-full object-cover"
                    />
                  ) : (
                    <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                      <UserIcon className="h-5 w-5 text-gray-500" />
                    </div>
                  )}
                  <div>
                    <div className="font-medium text-gray-900">{follower.username}</div>
                  </div>
                </div>
                <FollowButton
                  userId={follower.id}
                  isFollowing={follower.is_following}
                  size="sm"
                  onFollowChange={fetchFollowers}
                />
              </div>
            ))}

            {totalPages > 1 && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
              />
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}